<template>
  <div class="admin-call-positions-list px-2 px-md-4 py-2 py-md-4">
    <ConveyTable
      showSearch
      showCreate
      tableClassName="admin-positions-table"
      :items="positions"
      :fields="fields"
      :placeholders="placeholders"
      :perPageOptions="perPageOptions"
      :perPage="perPage"
      :currentPage="currentPage"
      :totalRows="totalRows"
      :isLoading="isFetchingPositions"
      :sortBy="sortBy"
      :sortDesc="sortDesc"
      @onFetch="(options) => fetchPositions(options)"
      @onCreate="addPosition"
    >
      <template #create-icon>
        <feather type="map-pin" size="16" />
      </template>
      <template #create-label>
        Create Position
      </template>
      <template #cell(position_name)="data">
        <router-link :to="`/admin/call-positions/${data.item.position_id}`">
          {{ data.value || "-" }}
        </router-link>
      </template>
      <template #cell(assigned_user)="data">
        <router-link
          v-if="data.item.user_id && $permission('read:user')"
          :to="`/admin/users/${data.item.user_id}`"
        >
          {{ data.value || "-" }}
        </router-link>
        <span v-else>
          {{ data.value || "-" }}
        </span>
      </template>
      <template #cell(created_at)="data">
        <span>{{ formatDate(data.value, "MMM D, YYYY h:mm a") }}</span>
      </template>
      <template #cell(updated_at)="data">
        <span>{{ formatDate(data.value, "MMM D, YYYY h:mm a") }}</span>
      </template>
      <template #cell(actions)="data">
        <div class="d-flex flex-nowrap justify-content-center align-items-center gap-3">
          <b-button
            class="p-0"
            style="background-color:transparent;border:none;"
            @click="removePosition(data.item)"
          >
            <p class="h4 m-0">
              <b-icon-trash title="Remove Call Position" variant="danger" />
            </p>
          </b-button>
          <b-button
            class="p-0"
            style="background-color:transparent;border:none;"
            @click="editPosition(data.item)"
          >
            <p class="h4 m-0">
              <b-icon-pencil-square title="Edit Call Position" variant="info" />
            </p>
          </b-button>
        </div>
      </template>
    </ConveyTable>
  </div>
</template>

<script>
/* eslint-disable */
import moment from 'moment';
import formatter from "@/mixins/formatter";
import errorHandler from "@/mixins/errorHandler";
import ConveyTable from "@/components/ConveyTable";

export default {
  name: "AdminCallPositionsList",
  mixins: [formatter, errorHandler],
  components: { ConveyTable },
  data() {
    return {
      positions: [],
      query: "",
      perPage: 10,
      perPageOptions: [5, 10, 25, 50, 100],
      currentPage: 1,
      totalRows: 0,
      isFetchingPositions: false,
      sortBy: "created_at",
      sortDesc: true,
      fields: [
        { key: "position_name", label: "Name", sortable: false },
        { key: "sip_id", label: "SIP ID", sortable: false },
        { key: "assigned_user", label: "Assigned User", sortable: false },
        { key: "created_at", label: "Created At", sortable: true },
        { key: "updated_at", label: "Updated At", sortable: false },
        { key: "actions", label: "Actions", sortable: false, stickyColumn: true, class: "right-sticky-column actions-column" },
      ],
      placeholders: {
        search: "Search position..."
      },
      fetchAbortController: null,
    };
  },
  methods: {
    abortFetchOperation() {
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
        this.fetchAbortController = null;
      }
    },
    async fetchPositions(fetchOptions) {
      try {
        this.abortFetchOperation();
        this.fetchAbortController = new AbortController();
        const {
          perPage,
          currentPage,
          sortBy,
          sortDesc
        } = fetchOptions;
        this.perPage = perPage;
        this.currentPage = currentPage;
        this.isFetchingPositions = true;
        this.sortBy = sortBy;
        this.sortDesc = sortDesc;

        const offset = (currentPage - 1) * perPage;
        const params = {
          offset,
          limit: this.perPage,
          order: sortBy,
          order_dir: sortDesc ? "desc" : "asc",
        };
        const resp = await this.$ConveyApi.agenciesApi.getAgencyPositions({
          params,
          signal: this.fetchAbortController.signal,
        });
        const {data, metadata} = resp.data;
        this.positions = data.map(item => {
          const {
            position_id,
            position_name,
            created_at,
            updated_at,
            sip_id,
            user_id,
            assigned_user,
          } = item;
          return {
            sip_id,
            position_id,
            position_name,
            user_id,
            assigned_user: assigned_user.trim(),
            created_at: this.formattedDate(created_at),
            updated_at: this.formattedDate(updated_at),
          };
        });
        this.totalRows = metadata ? metadata.totalRows || 0 : 0;
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[AdminCallPositionsList:fetchPositions] ERROR: ", err);
        }
      } finally {
        this.isFetchingPositions = false;
      }
    },
    addPosition() {
      this.$router.push({ name: "Admin Create Call Position" }).catch(() => {});
    },
    async removePosition(position) {
      if (!confirm(`Are you sure you want to remove "${position.sip_id} ${position.position_name}" call position from your agency?`)) {
        return;
      }

      try {
        await this.$ConveyApi.agenciesApi.removeAgencyPosition({ positionId: position.position_id });
        this.$toastr("success", "Call position has been removed from your agency.");
      } catch(err) {
        console.log("[AdminCallPositionsList:removePosition] ERROR: ", err);
        this.$toastr("error", "Error occurred removing call position from your agency");
      } finally {
        this.fetchPositions({
          perPage: this.perPage,
          currentPage: 1,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
        });
      }
    },
    editPosition(position) {
      this.$router.push({
        name: "Admin Edit Call Position",
        params: {
          positionId: position.position_id,
        },
      }).catch(() => {});
    },
    formattedDate(date) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const formattedDate = moment(date).valueOf();
      return formattedDate;
    },
  },
  beforeDestroy() {
    this.abortFetchOperation();
  }
};
</script>

<style lang="scss">
.admin-call-positions-list {
  width: 100%;
  height: 100%;
}
</style>